/**
 * A time field that is 24-hour on every machine.
 *
 * WHY NOT <input type="time">
 *
 * Because it cannot be made to obey. Chrome formats that control from the
 * BROWSER's locale and ignores the element's `lang` -- verified against
 * Chrome 147, where `lang="en-GB"`, `lang="de"` and a plain input all render
 * "01:05 PM" for the same 13:05 value. No CSS or script reaches inside it
 * either: the AM/PM is drawn by the browser's own shadow tree.
 *
 * An earlier attempt tagged twenty-two of these with `lang="en-GB"` on the
 * strength of that claim. It shipped, it passed a source-reading check, and
 * every picker in the laboratory went on printing AM/PM -- which is the one
 * thing a clinical time may not do, because the half that says which twelve
 * is the half that gets dropped when a time is read aloud, copied onto a
 * tube, or faxed.
 *
 * So the control is ours. It is a text field that speaks HH:mm and nothing
 * else, and it is forgiving about how the bench types:
 *
 *      1305   ->  13:05        930   ->  09:30
 *      13:5   ->  13:05        7     ->  07:00
 *      2530   ->  refused, the field stays as it was
 *
 * The VALUE is always `HH:mm` or '', which is exactly what the native
 * control emitted, so nothing downstream changes.
 *
 * What is lost is the mobile wheel picker. That is the price of a time that
 * means one thing, and the bench types times far more often than it spins
 * them.
 */
import React, { useEffect, useState } from 'react';

/** "1305" | "13:5" | "7" -> "13:05" | null when it cannot be read. */
export function parseTime24(raw) {
  const t = String(raw ?? '').trim();
  if (!t) return '';
  // Digits only, so a pasted "13:05:00" or "1.05" still lands.
  const d = t.replace(/\D/g, '');
  if (!d) return null;
  let hh, mm;
  if (d.length <= 2) { hh = d; mm = '00'; }            // "7" -> 07:00
  else if (d.length === 3) { hh = d.slice(0, 1); mm = d.slice(1); }   // 930
  else { hh = d.slice(0, 2); mm = d.slice(2, 4); }     // 1305
  const H = parseInt(hh, 10);
  const M = parseInt(mm, 10);
  if (!Number.isFinite(H) || !Number.isFinite(M)) return null;
  if (H > 23 || M > 59) return null;
  return `${String(H).padStart(2, '0')}:${String(M).padStart(2, '0')}`;
}

export default function Time24({
  value, onChange, disabled = false, style, required = false,
  autoFocus = false, id, className, placeholder = 'HH:MM', title,
}) {
  // Typing is held locally so a half-written "13:" is not thrown away on
  // every keystroke; the committed value only moves on blur or Enter.
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = (raw) => {
    const parsed = parseTime24(raw);
    if (parsed === null) { setDraft(value || ''); return; }  // unreadable — put it back
    setDraft(parsed);
    if (parsed !== (value || '')) onChange?.(parsed);
  };

  return (
    <input
      id={id}
      className={className}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      placeholder={placeholder}
      title={title || '24-hour clock — type 1305 or 13:05'}
      value={draft}
      disabled={disabled}
      required={required}
      autoFocus={autoFocus}
      onChange={(e) => {
        // Allow only what a time can contain while it is being typed, and
        // drop the colon in for them after two digits.
        let v = e.target.value.replace(/[^\d:]/g, '').slice(0, 5);
        if (/^\d{3}$/.test(v)) v = `${v.slice(0, 2)}:${v.slice(2)}`;
        setDraft(v);
      }}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(e.currentTarget.value); }}
      style={style}
    />
  );
}
